import { defineStore } from 'pinia'
import { ref, computed } from 'vue'
import { useRouter } from 'vue-router'
import AuthService from '@/services/authService'
import { setSentryUser, clearSentryUser, addSentryBreadcrumb } from '@/services/sentry'
import type {
  User,
  LoginRequest,
  RegisterRequest,
  AuthResponse,
  ForgotPasswordRequest,
  ResetPasswordRequest,
  UpdateProfileRequest,
  ROLES,
  PERMISSIONS,
  TwoFactorMethod,
  TwoFactorVerifyRequest
} from '@/types/auth'
import { useServiceStore } from './service'
import { useProviderStore } from './provider'
import { useViewingProviderStore } from './viewingProvider'
import { useShopStore } from './shop'
import { useProductStore } from './product'
import { useShopOrderStore } from './shopOrder'
import { useCartStore } from './cart'
import { useActivityTracker } from '@/composables/useActivityTracker'

export const useAuthStore = defineStore('auth', () => {
  // State
  const user = ref<User | null>(null)
  const accessToken = ref<string | null>(null)
  const refreshToken = ref<string | null>(null)
  const isLoading = ref(false)
  const loginAttempts = ref(0)
  const lastLoginAttempt = ref<number | null>(null)
  const isRefreshing = ref(false)
  const refreshPromise = ref<Promise<boolean> | null>(null)
  const refreshScheduleId = ref<number | null>(null)

  // 2FA State
  const requiresTwoFactor = ref(false)
  const twoFactorUserId = ref<string | null>(null)
  const twoFactorMethod = ref<TwoFactorMethod | null>(null)

  // Router
  const router = useRouter()

  // Getters
  const isAuthenticated = computed(() => !!accessToken.value && !!user.value)
  
  const userRoles = computed(() => user.value?.roles || [])
  
  const userPermissions = computed(() => user.value?.permissions || [])
  
  const userName = computed(() => user.value?.fullName || user.value?.firstName || 'User')
  
  const userInitials = computed(() => {
    if (!user.value) return 'U'
    const first = user.value.firstName?.[0] || ''
    const last = user.value.surname?.[0] || ''
    return (first + last).toUpperCase() || 'U'
  })

  const isEmailVerified = computed(() => user.value?.emailVerified ?? false)
  
  const isTwoFactorEnabled = computed(() => user.value?.twoFactorEnabled ?? false)

  const isAdmin = computed(() => userRoles.value.includes('Admin'))

  const isProvider = computed(() => userRoles.value.includes('Provider'))

  const isSystemAdmin = computed(() => userRoles.value.includes('SystemAdmin'))

  // Rate limiting for login attempts
  const canAttemptLogin = computed(() => {
    if (loginAttempts.value < 3) return true
    if (!lastLoginAttempt.value) return true
    
    const timeSinceLastAttempt = Date.now() - lastLoginAttempt.value
    const cooldownPeriod = Math.min(300000, Math.pow(2, loginAttempts.value - 3) * 60000) // Exponential backoff, max 5 min
    
    return timeSinceLastAttempt > cooldownPeriod
  })

  const loginCooldownTime = computed(() => {
    if (canAttemptLogin.value) return 0
    if (!lastLoginAttempt.value) return 0
    
    const cooldownPeriod = Math.min(300000, Math.pow(2, loginAttempts.value - 3) * 60000)
    const timeSinceLastAttempt = Date.now() - lastLoginAttempt.value
    
    return Math.ceil((cooldownPeriod - timeSinceLastAttempt) / 1000)
  })

  const isTokenExpired = computed(() => {
    if (!accessToken.value) return true

    try {
      const tokenPayload = JSON.parse(atob(accessToken.value.split('.')[1]))
      const expirationTime = tokenPayload.exp * 1000
      const currentTime = Date.now()

      // Consider token expired 5 seconds before actual expiry (reduced from 30s)
      // This small buffer accounts for clock skew and network latency
      return currentTime >= (expirationTime - 5000)
    } catch (error) {
      console.error('Error parsing token:', error)
      return true
    }
  })

  const isTokenNearExpiry = computed(() => {
    if (!accessToken.value) return false

    try {
      const tokenPayload = JSON.parse(atob(accessToken.value.split('.')[1]))
      const expirationTime = tokenPayload.exp * 1000
      const currentTime = Date.now()

      // Consider near expiry if less than 60 seconds remaining (reduced from 2 minutes)
      // This triggers proactive refresh 1 minute before expiry
      return currentTime >= (expirationTime - 60000)
    } catch (error) {
      console.error('Error parsing token:', error)
      return true
    }
  })

  // Actions
  async function login(credentials: LoginRequest): Promise<boolean> {
    if (!canAttemptLogin.value) {
      return false
    }

    isLoading.value = true

    try {
      // Include persistent device token for 2FA "remember device" bypass
      const deviceToken = localStorage.getItem('zaveato_device_token')
      const response = await AuthService.login({
        ...credentials,
        deviceToken: deviceToken || undefined
      })

      // ─── Cross-region login redirect ─────────────────────────────────
      // Backend detected user belongs to a different region's domain.
      // No tokens issued. Show a friendly toast then bounce the browser
      // to their home domain's login page (email is prefilled server-side
      // in the URL). We do NOT setAuthData — there's nothing to store.
      if (response.requiresRegionRedirect && response.regionRedirectUrl) {
        const targetLabel = response.userRegionDisplayName || 'your regional site'
        // Lightweight toast — no toast library needed; the browser will
        // navigate away in ~1s. If any Vue toast/plugin is preferred
        // globally, swap this for that. Deliberately using window.alert
        // as a fallback path would break UX; a silent redirect is also
        // fine because the target page shows the same context.
        if (typeof window !== 'undefined') {
          try {
            // Best-effort inline banner — non-blocking, disappears with
            // the redirect. Wrapped in a try in case document.body is
            // gated by SSR or the current view has replaced it.
            const banner = document.createElement('div')
            banner.textContent = `Your account belongs to ${targetLabel} — redirecting…`
            banner.style.cssText =
              'position:fixed;top:16px;left:50%;transform:translateX(-50%);' +
              'background:#111827;color:#fff;padding:12px 20px;border-radius:8px;' +
              'font-family:system-ui,sans-serif;font-size:14px;z-index:99999;' +
              'box-shadow:0 4px 20px rgba(0,0,0,.25);'
            document.body.appendChild(banner)
          } catch { /* fall through to redirect */ }

          // Slight delay so the user reads the toast before we navigate.
          window.setTimeout(() => {
            window.location.href = response.regionRedirectUrl!
          }, 900)
        }

        // Reset attempt counter — this isn't a failure, credentials were
        // valid. Return true so the login button doesn't flash an error.
        loginAttempts.value = 0
        lastLoginAttempt.value = null
        return true
      }

      // Check if email verification is required
      if (response.requiresEmailVerification) {
        // Store user data temporarily without tokens
        user.value = response.user!

        // Navigate to email verification page
        await router.push({ name: 'EmailVerificationRequired' })

        // Reset login attempts since credentials were correct
        loginAttempts.value = 0
        lastLoginAttempt.value = null

        return true
      }

      // Check if 2FA is required
      if (response.requiresTwoFactor) {
        requiresTwoFactor.value = true
        twoFactorUserId.value = response.twoFactorUserId!
        twoFactorMethod.value = response.twoFactorMethod!

        // Don't reset login attempts yet - wait for 2FA verification
        return true
      }

      // Normal login flow - set auth data
      await setAuthData(response)

      // Reset login attempts on successful login
      loginAttempts.value = 0
      lastLoginAttempt.value = null

      // Redirect to intended page or role-based dashboard
      await redirectAfterLogin(response)

      return true
    } catch (error: any) {
      // Increment login attempts
      loginAttempts.value++
      lastLoginAttempt.value = Date.now()

      return false
    } finally {
      isLoading.value = false
    }
  }

  // Verify 2FA code and complete login
  async function verifyTwoFactor(code: string, rememberDevice: boolean = false): Promise<boolean> {
    if (!twoFactorUserId.value) {
      return false
    }

    isLoading.value = true

    try {
      const request: TwoFactorVerifyRequest = {
        userId: twoFactorUserId.value,
        code: code,
        rememberDevice: rememberDevice
      }

      const response = await AuthService.verifyTwoFactor(request)

      // Save device token for future login 2FA bypass
      if (response.deviceToken) {
        localStorage.setItem('zaveato_device_token', response.deviceToken)
      }

      // Set auth data and redirect
      await setAuthData(response)

      // Reset login attempts and 2FA state
      loginAttempts.value = 0
      lastLoginAttempt.value = null
      requiresTwoFactor.value = false
      twoFactorUserId.value = null
      twoFactorMethod.value = null

      // Redirect after successful 2FA
      await redirectAfterLogin(response)

      return true
    } catch (error: any) {
      // Don't increment login attempts for 2FA failures
      return false
    } finally {
      isLoading.value = false
    }
  }

  // Send email code for 2FA during login
  async function sendEmailCodeForLogin(): Promise<boolean> {
    if (!twoFactorUserId.value) {
      return false
    }

    try {
      await AuthService.sendEmailCodeForLogin(twoFactorUserId.value)
      return true
    } catch (error: any) {
      return false
    }
  }

  // Helper function to redirect after successful login
  async function redirectAfterLogin(response: AuthResponse): Promise<void> {
    const redirectTo = router.currentRoute.value.query.redirect as string
    if (redirectTo) {
      await router.push(redirectTo)
    } else {
      // Redirect based on user role
      if (response.user?.roles.includes('Admin')) {
        await router.push('/admin')
      } else if (response.user?.roles.includes('Provider')) {
        await router.push('/provider')
      } else if (response.user?.roles.includes('ServiceUser')) {
        await router.push('/service-user/dashboard')
      } else if (response.user?.roles.includes('ShopStaff')) {
        await router.push('/shop/dashboard')
      } else {
        await router.push('/dashboard')
      }
    }
  }

  async function register(userData: RegisterRequest): Promise<{ success: boolean; errors?: Record<string, string>; message?: string }> {
    isLoading.value = true

    try {
      const response = await AuthService.register(userData)

      // Check if email verification is required
      if (response.requiresEmailVerification) {
        // Store user data temporarily without tokens
        user.value = response.user!

        // Navigate to email verification page
        await router.push({ name: 'EmailVerificationRequired' })

        return {
          success: true,
          message: 'Registration successful! Please check your email to verify your account.'
        }
      }

      // If email verification is NOT required (shouldn't happen normally)
      await setAuthData(response)

      // Redirect based on user role
      if (response.user?.roles.includes('Admin')) {
        router.push('/admin')
      } else if (response.user?.roles.includes('Provider')) {
        router.push('/provider')
      } else if (response.user?.roles.includes('ServiceUser')) {
        router.push('/service-user/dashboard')
      } else if (response.user?.roles.includes('ShopStaff')) {
        router.push('/shop/dashboard')
      } else {
        router.push('/dashboard')
      }

      return { success: true }
    } catch (error: any) {
      console.log('=== AUTH STORE ERROR DEBUG ===')
      console.log('Full error object:', error)
      console.log('error.response:', error.response)
      console.log('error.response?.data:', error.response?.data)
      console.log('error.response?.data?.errors:', error.response?.data?.errors)
      console.log('error.data:', error.data)
      console.log('error.status:', error.status)
      console.log('=== END DEBUG ===')

      // Try to extract errors from different possible structures
      let errors = null
      let errorMessage = 'Registration failed'

      // Check multiple possible locations for validation errors
      if (error.response?.data?.errors) {
        errors = error.response.data.errors
        errorMessage = error.response.data.message || 'Validation failed'
      } else if (error.data?.errors) {
        errors = error.data.errors
        errorMessage = error.data.message || 'Validation failed'
      }

      if (errors) {
        console.log('Found validation errors:', errors)
        return {
          success: false,
          errors: errors
        }
      }

      // Extract error message
      if (error.response?.data?.message) {
        errorMessage = error.response.data.message
      } else if (error.data?.message) {
        errorMessage = error.data.message
      } else if (error.message) {
        errorMessage = error.message
      }

      console.log('Returning generic error:', errorMessage)
      return {
        success: false,
        message: errorMessage
      }
    } finally {
      isLoading.value = false
    }
  }

  async function logout(_showMessage = true, redirectUrl?: string): Promise<void> {
    isLoading.value = true

    try {
      if (isAuthenticated.value && accessToken.value) {
        // Attempt to call logout endpoint, but don't fail if it doesn't work
        try {
          await AuthService.logout()
        } catch (logoutError) {
          console.warn('Logout API call failed, but proceeding with local logout:', logoutError)
          // Continue with local logout even if API call fails
        }
      }
    } catch (error) {
      console.error('Logout error:', error)
      // Even if there's an error, we should still clear local auth data
    } finally {
      // Always clear auth data and redirect, regardless of API call success
      clearAuthData()

      // Use replace instead of push to avoid back button issues.
      // If redirectUrl provided (e.g., from session expiry), include it as
      // a query param — UNLESS it's an auth route itself (login, register,
      // forgot-password, callback). Sending the user back to /auth/register
      // after a successful login is a circular nonsense flow, so we strip
      // those targets and fall through to the default post-login route.
      const isAuthPath = !!redirectUrl && redirectUrl.startsWith('/auth/')
      if (redirectUrl && !isAuthPath) {
        await router.replace({
          path: '/auth/login',
          query: { redirect: redirectUrl }
        })
      } else {
        await router.replace('/auth/login')
      }
      isLoading.value = false
    }
  }

  async function refreshTokens(force: boolean = false): Promise<boolean> {
    // If already refreshing, return the existing promise
    if (isRefreshing.value && refreshPromise.value) {
      return refreshPromise.value
    }

    // Don't refresh if no refresh token available
    if (!refreshToken.value) {
      clearAuthData()
      return false
    }

    // Don't refresh if token is not expired or near expiry
    // UNLESS force=true (e.g., when we got a 401 from the server)
    if (!force && !isTokenExpired.value && !isTokenNearExpiry.value) {
      return true
    }

    // Set refreshing state and create promise
    isRefreshing.value = true
    refreshPromise.value = performTokenRefresh()

    return refreshPromise.value
  }

  async function performTokenRefresh(): Promise<boolean> {
    // SECURITY: Check if user has been inactive for too long
    // If inactive for more than 1 hour, don't refresh - force re-login
    const { isInactive, getInactiveTime } = useActivityTracker()

    if (isInactive()) {
      const inactiveMinutes = Math.round(getInactiveTime() / 60000)
      console.warn(`🔒 Session expired due to inactivity (${inactiveMinutes} minutes). Redirecting to login.`)

      // Clear auth data and redirect to login
      clearAuthData()

      // Redirect with message about session expiry.
      // Strip auth-route paths from the redirect target — sending the user
      // back to /auth/* after re-login is nonsense (e.g., back to register
      // when they just wanted to log in). Falls through to default post-login.
      const currentPath = router.currentRoute.value.fullPath
      const safeRedirect = currentPath && !currentPath.startsWith('/auth/') ? currentPath : undefined
      await router.replace({
        path: '/auth/login',
        query: safeRedirect
          ? { redirect: safeRedirect, reason: 'inactivity' }
          : { reason: 'inactivity' }
      })

      return false
    }

    try {
      console.log('🔄 Refreshing access token...')

      const response = await AuthService.refreshToken({
        refreshToken: refreshToken.value!
      })

      await setAuthData(response)
      console.log('✅ Token refreshed successfully')

      // Clear scheduled refresh if exists
      if (refreshScheduleId.value) {
        clearTimeout(refreshScheduleId.value)
        refreshScheduleId.value = null
      }

      // Schedule next refresh
      scheduleTokenRefresh()

      return true
    } catch (error: any) {
      console.error('❌ Token refresh failed:', error)

      // Any definitive client-side rejection from /auth/refresh means the
      // refresh token isn't going to start working — retrying just burns
      // rate-limit budget and locks the caller in a loop. Includes:
      //   400 — "Invalid or expired refresh token"
      //   401/403 — explicit auth failure
      //   429 — we've already blown the rate limit; further calls will fail too
      // Only 5xx / network errors are safe to leave for a manual retry.
      const status = error?.response?.status
      const isTerminal = status !== undefined && status >= 400 && status < 500
      if (isTerminal) {
        console.warn(`🚪 Refresh rejected with ${status}, clearing auth data`)
        clearAuthData()
      } else {
        console.warn('⚠️ Token refresh failed due to network/server error, keeping auth data for retry')
      }

      return false
    } finally {
      isRefreshing.value = false
      refreshPromise.value = null
    }
  }

  async function forgotPassword(emailOrData: string | ForgotPasswordRequest): Promise<boolean> {
    isLoading.value = true

    try {
      const requestData = typeof emailOrData === 'string'
        ? { email: emailOrData }
        : emailOrData

      await AuthService.forgotPassword(requestData)
      return true
    } catch (error: any) {
      return false
    } finally {
      isLoading.value = false
    }
  }

  async function resetPassword(data: ResetPasswordRequest): Promise<{ success: boolean; error?: string }> {
    isLoading.value = true

    try {
      await AuthService.resetPassword(data)
      // Don't redirect here - let the calling component handle navigation
      return { success: true }
    } catch (error: any) {
      // Log full error for debugging
      console.error('Password reset error:', {
        status: error?.response?.status,
        statusText: error?.response?.statusText,
        data: error?.response?.data,
        message: error?.message
      })

      // Extract error message from response
      const errorMessage = error?.response?.data?.message ||
                           error?.message ||
                           'Failed to reset password. Please try again.'

      console.log('Extracted error message:', errorMessage)
      return { success: false, error: errorMessage }
    } finally {
      isLoading.value = false
    }
  }

  async function verifyEmail(token: string): Promise<{ success: boolean; requiresPasswordReset?: boolean; passwordResetToken?: string }> {
    isLoading.value = true

    try {
      const result = await AuthService.verifyEmail(token)

      // Update user data to reflect verified email
      if (user.value) {
        user.value.emailVerified = true
      }

      return {
        success: true,
        requiresPasswordReset: result.requiresPasswordReset,
        passwordResetToken: result.passwordResetToken
      }
    } catch (error: any) {
      return { success: false }
    } finally {
      isLoading.value = false
    }
  }

  async function resendVerificationEmail(brand?: string): Promise<boolean> {
    isLoading.value = true

    try {
      if (!user.value?.email) {
        return false
      }

      await AuthService.resendVerificationEmail(user.value.email, brand)
      return true
    } catch (error: any) {
      return false
    } finally {
      isLoading.value = false
    }
  }

  async function getCurrentUser(): Promise<boolean> {
    if (!accessToken.value) return false

    try {
      const userData = await AuthService.getCurrentUser()

      // Safety: preserve existing roles if the response has none
      // This prevents role loss from endpoints that don't load roles
      if (user.value?.roles?.length && (!userData.roles || userData.roles.length === 0)) {
        console.warn('getCurrentUser: Response has no roles, preserving existing roles:', user.value.roles)
        userData.roles = user.value.roles
        userData.permissions = user.value.permissions || []
      }

      user.value = userData
      localStorage.setItem('user', JSON.stringify(userData))
      return true
    } catch (error) {
      console.error('Failed to get current user:', error)
      return false
    }
  }

  async function updateProfile(userData: UpdateProfileRequest): Promise<boolean> {
    isLoading.value = true

    try {
      const updatedUser = await AuthService.updateProfile(userData as unknown as Partial<User>)
      user.value = updatedUser

      // Update stored user data
      localStorage.setItem('user', JSON.stringify(updatedUser))

      return true
    } catch (error: any) {
      console.error('Auth Store: updateProfile error:', error)
      return false
    } finally {
      isLoading.value = false
    }
  }

  // Permission and role checking
  function hasRole(role: keyof typeof ROLES): boolean {
    return userRoles.value.includes(role)
  }

  function hasAnyRole(roles: Array<keyof typeof ROLES>): boolean {
    return roles.some(role => userRoles.value.includes(role))
  }

  function hasPermission(permission: keyof typeof PERMISSIONS): boolean {
    return userPermissions.value.includes(permission)
  }

  function hasAnyPermission(permissions: Array<keyof typeof PERMISSIONS>): boolean {
    return permissions.some(permission => userPermissions.value.includes(permission))
  }

  function hasAllPermissions(permissions: Array<keyof typeof PERMISSIONS>): boolean {
    return permissions.every(permission => userPermissions.value.includes(permission))
  }

  // Helper functions
  async function setAuthData(authResponse: AuthResponse): Promise<void> {
    // Only set auth data if user is present (not a 2FA pending response)
    if (!authResponse.user) {
      return
    }

    // SECURITY: Only clear stores when user changes (login/switch user)
    // Do NOT clear on token refresh — this preserves shop/order data
    const previousUserId = user.value?.id
    const newUserId = authResponse.user.id
    if (!previousUserId || previousUserId !== newUserId) {
      clearDependentStores()
      console.log('✅ All stores cleared — new user login')
    }

    user.value = authResponse.user
    accessToken.value = authResponse.accessToken
    refreshToken.value = authResponse.refreshToken

    // Store in localStorage for persistence across page refreshes
    localStorage.setItem('accessToken', authResponse.accessToken)
    localStorage.setItem('refreshToken', authResponse.refreshToken)
    localStorage.setItem('user', JSON.stringify(authResponse.user))

    // Tag Sentry events with the signed-in user
    setSentryUser({
      id: authResponse.user.id,
      email: authResponse.user.email,
      roles: (authResponse.user as any).roles,
    })
    addSentryBreadcrumb('User signed in', 'auth', { userId: authResponse.user.id })

    // Reset activity timestamp on login (user is active now)
    const { resetActivity } = useActivityTracker()
    resetActivity()

    // Broadcast login to other tabs via localStorage event
    // Use a small delay before cleanup to ensure other tabs receive the event
    const loginEvent = JSON.stringify({
      type: 'login',
      userId: authResponse.user.id,
      timestamp: Date.now()
    })
    localStorage.setItem('auth_event', loginEvent)
    // Clean up after a brief delay to avoid race conditions
    setTimeout(() => {
      // Only remove if it's still our event (avoid removing newer events)
      if (localStorage.getItem('auth_event') === loginEvent) {
        localStorage.removeItem('auth_event')
      }
    }, 100)

    // Schedule token refresh
    scheduleTokenRefresh()
  }

  function clearAuthData(): void {
    addSentryBreadcrumb('User signed out / auth cleared', 'auth')
    clearSentryUser()
    user.value = null
    accessToken.value = null
    refreshToken.value = null
    isRefreshing.value = false
    refreshPromise.value = null

    // Clear 2FA state
    requiresTwoFactor.value = false
    twoFactorUserId.value = null
    twoFactorMethod.value = null

    // Clear scheduled refresh
    if (refreshScheduleId.value) {
      clearTimeout(refreshScheduleId.value)
      refreshScheduleId.value = null
    }

    // IMPORTANT: Preserve booking context if it exists
    // This allows users to complete their booking after re-authentication
    const pendingBookingContext = sessionStorage.getItem('pendingBookingContext')

    // Clear localStorage
    localStorage.removeItem('accessToken')
    localStorage.removeItem('refreshToken')
    localStorage.removeItem('user')

    // Clear activity tracking data
    const { clearActivity } = useActivityTracker()
    clearActivity()

    // Broadcast logout to other tabs via localStorage event
    // Use a small delay before cleanup to ensure other tabs receive the event
    const logoutEvent = JSON.stringify({
      type: 'logout',
      timestamp: Date.now()
    })
    localStorage.setItem('auth_event', logoutEvent)
    // Clean up after a brief delay to avoid race conditions
    setTimeout(() => {
      // Only remove if it's still our event (avoid removing newer events)
      if (localStorage.getItem('auth_event') === logoutEvent) {
        localStorage.removeItem('auth_event')
      }
    }, 100)

    // Restore booking context after clearing
    if (pendingBookingContext) {
      sessionStorage.setItem('pendingBookingContext', pendingBookingContext)
      console.log('✅ Booking context preserved during logout')
    }

    // SECURITY FIX: Clear all other stores to prevent data leakage between users
    // This prevents Provider 1's data from showing to Provider 2 on login
    try {
      const serviceStore = useServiceStore()
      const providerStore = useProviderStore()
      const viewingProviderStore = useViewingProviderStore()
      const shopStore = useShopStore()
      const productStore = useProductStore()
      const shopOrderStore = useShopOrderStore()
      const cartStore = useCartStore()

      // Reset stores to clear cached data
      if (serviceStore.$reset) {
        serviceStore.$reset()
        console.log('✅ Service store cleared')
      }
      if (providerStore.resetStore) {
        providerStore.resetStore()
        console.log('✅ Provider store cleared')
      }
      if (viewingProviderStore.$reset) {
        viewingProviderStore.$reset()
        console.log('✅ ViewingProvider store cleared')
      }
      // SECURITY FIX: Clear Shop stores to prevent data leakage
      if (shopStore.resetStore) {
        shopStore.resetStore()
        console.log('✅ Shop store cleared')
      }
      if (productStore.resetStore) {
        productStore.resetStore()
        console.log('✅ Product store cleared')
      }
      if (shopOrderStore.resetStore) {
        shopOrderStore.resetStore()
        console.log('✅ ShopOrder store cleared')
      }
      // SECURITY FIX: Clear cart store to prevent cart data leakage between users
      if (cartStore.resetStore) {
        cartStore.resetStore()
        console.log('✅ Cart store cleared')
      }

      console.log('✅ All stores cleared on logout')
    } catch (error) {
      console.error('Error clearing stores on logout:', error)
    }
  }

  // Initialize auth state from localStorage
  function initializeAuth(): void {
    // Initialize activity tracking for session timeout
    const { initializeTracking } = useActivityTracker()
    initializeTracking()

    const storedAccessToken = localStorage.getItem('accessToken')
    const storedRefreshToken = localStorage.getItem('refreshToken')
    const storedUser = localStorage.getItem('user')

    if (storedAccessToken && storedRefreshToken && storedUser) {
      try {
        // CRITICAL FIX: Clear all dependent stores BEFORE setting auth data
        // This prevents stale data from previous sessions contaminating the current session
        // Without this, users would see old services/shop data until they hard refresh (CTRL+SHIFT+R)
        clearDependentStores()

        accessToken.value = storedAccessToken
        refreshToken.value = storedRefreshToken
        const parsedUser = JSON.parse(storedUser)
        user.value = parsedUser

        // Restore Sentry user tag for this session
        if (parsedUser?.id) {
          setSentryUser({
            id: parsedUser.id,
            email: parsedUser.email,
            roles: parsedUser.roles,
          })
        }

        console.log('🔐 Auth initialized from localStorage, stores cleared')

        // Schedule token refresh if token is valid, or refresh immediately if expired
        if (!isTokenExpired.value) {
          scheduleTokenRefresh()
        } else if (storedRefreshToken) {
          // Token is expired but we have a refresh token — refresh immediately
          // This prevents the first API calls from getting 401s on page load
          console.warn('⚠️ Stored access token is expired, refreshing immediately...')
          refreshTokens(true).then(success => {
            if (success) {
              console.log('✅ Token refreshed on init')
            } else {
              console.warn('⚠️ Init token refresh failed — 401 handler will retry')
            }
          })
        }
      } catch (error) {
        console.error('Failed to parse stored user data:', error)
        clearAuthData()
      }
    }

    // Setup multi-tab session synchronization
    setupStorageSync()
  }

  // Clear all dependent stores to prevent stale data
  // Used when initializing from localStorage or when user changes
  function clearDependentStores(): void {
    try {
      const serviceStore = useServiceStore()
      const providerStore = useProviderStore()
      const viewingProviderStore = useViewingProviderStore()
      const shopStore = useShopStore()
      const productStore = useProductStore()
      const shopOrderStore = useShopOrderStore()
      const cartStore = useCartStore()

      if (serviceStore.$reset) {
        serviceStore.$reset()
      }
      if (providerStore.resetStore) {
        providerStore.resetStore()
      }
      if (viewingProviderStore.$reset) {
        viewingProviderStore.$reset()
      }
      if (shopStore.resetStore) {
        shopStore.resetStore()
      }
      if (productStore.resetStore) {
        productStore.resetStore()
      }
      if (shopOrderStore.resetStore) {
        shopOrderStore.resetStore()
      }
      if (cartStore.resetStore) {
        cartStore.resetStore()
      }

      console.log('✅ All dependent stores cleared')
    } catch (error) {
      console.error('Error clearing dependent stores:', error)
    }
  }

  // Multi-tab session synchronization
  // Listens for localStorage changes from other tabs to keep session state consistent
  function setupStorageSync(): void {
    window.addEventListener('storage', (event) => {
      // Handle auth events broadcast from other tabs
      if (event.key === 'auth_event' && event.newValue) {
        try {
          const authEvent = JSON.parse(event.newValue)
          console.log(`🔄 Auth event detected from another tab: ${authEvent.type}`)

          if (authEvent.type === 'logout') {
            console.log('🚪 Logout detected in another tab, syncing...')
            handleLogoutFromOtherTab()
          } else if (authEvent.type === 'login') {
            const currentUserId = user.value?.id

            // Check if it's a different user logging in
            if (currentUserId && authEvent.userId !== currentUserId) {
              console.log('👤 Different user logged in from another tab, syncing...')
              handleUserChangeFromOtherTab()
            } else if (!currentUserId) {
              // Current tab was logged out, sync the new login
              console.log('🔑 Login detected in another tab, syncing...')
              handleLoginFromOtherTab()
            }
          }
        } catch (error) {
          console.error('Failed to parse auth event:', error)
        }
        return
      }

      // Handle accessToken changes (logout detection)
      if (event.key === 'accessToken') {
        if (event.newValue === null && user.value) {
          // Token removed - logout from another tab
          console.log('🚪 Access token removed in another tab, syncing logout...')
          handleLogoutFromOtherTab()
        }
        return
      }

      // Handle user data changes
      if (event.key === 'user') {
        const currentUserId = user.value?.id

        if (event.newValue === null && currentUserId) {
          // User data removed - logout
          console.log('🚪 User data removed in another tab, syncing logout...')
          handleLogoutFromOtherTab()
        } else if (event.newValue && !currentUserId) {
          // User data added - login from another tab
          console.log('🔑 User data added in another tab, syncing login...')
          handleLoginFromOtherTab()
        } else if (event.newValue) {
          try {
            const newUser = JSON.parse(event.newValue)
            if (currentUserId && newUser.id !== currentUserId) {
              console.log('👤 Different user detected in another tab, syncing...')
              handleUserChangeFromOtherTab()
            }
          } catch (error) {
            console.error('Failed to parse user data from storage event:', error)
          }
        }
      }
    })

    console.log('✅ Multi-tab session sync initialized')
  }

  // Handle logout triggered from another tab
  function handleLogoutFromOtherTab(): void {
    // Clear local state without calling logout() to avoid API call and redirect loop
    user.value = null
    accessToken.value = null
    refreshToken.value = null
    isRefreshing.value = false
    refreshPromise.value = null
    requiresTwoFactor.value = false
    twoFactorUserId.value = null
    twoFactorMethod.value = null

    // Clear scheduled refresh
    if (refreshScheduleId.value) {
      clearTimeout(refreshScheduleId.value)
      refreshScheduleId.value = null
    }

    // Clear other stores
    try {
      const serviceStore = useServiceStore()
      const providerStore = useProviderStore()
      const viewingProviderStore = useViewingProviderStore()
      const shopStore = useShopStore()
      const productStore = useProductStore()
      const shopOrderStore = useShopOrderStore()
      const cartStore = useCartStore()

      if (serviceStore.$reset) serviceStore.$reset()
      if (providerStore.resetStore) providerStore.resetStore()
      if (viewingProviderStore.$reset) viewingProviderStore.$reset()
      // SECURITY FIX: Clear Shop stores
      if (shopStore.resetStore) shopStore.resetStore()
      if (productStore.resetStore) productStore.resetStore()
      if (shopOrderStore.resetStore) shopOrderStore.resetStore()
      if (cartStore.resetStore) cartStore.resetStore()
    } catch (error) {
      console.error('Error clearing stores on cross-tab logout:', error)
    }

    // Redirect to login
    router.replace('/auth/login')
  }

  // Handle different user logging in from another tab
  function handleUserChangeFromOtherTab(): void {
    // Clear current user's data from stores first
    try {
      const serviceStore = useServiceStore()
      const providerStore = useProviderStore()
      const viewingProviderStore = useViewingProviderStore()
      const shopStore = useShopStore()
      const productStore = useProductStore()
      const shopOrderStore = useShopOrderStore()
      const cartStore = useCartStore()

      if (serviceStore.$reset) serviceStore.$reset()
      if (providerStore.resetStore) providerStore.resetStore()
      if (viewingProviderStore.$reset) viewingProviderStore.$reset()
      // SECURITY FIX: Clear Shop stores
      if (shopStore.resetStore) shopStore.resetStore()
      if (productStore.resetStore) productStore.resetStore()
      if (shopOrderStore.resetStore) shopOrderStore.resetStore()
      if (cartStore.resetStore) cartStore.resetStore()
    } catch (error) {
      console.error('Error clearing stores on cross-tab user change:', error)
    }

    // Force page reload to sync with the new user session
    // The reload will trigger initializeAuth which will read from localStorage
    console.log('🔄 Reloading page to sync with new user session...')
    window.location.reload()
  }

  // Handle login from another tab when current tab was logged out
  function handleLoginFromOtherTab(): void {
    // Read the new auth data from localStorage
    const newToken = localStorage.getItem('accessToken')
    const newRefreshToken = localStorage.getItem('refreshToken')
    const newUserData = localStorage.getItem('user')

    if (newToken && newRefreshToken && newUserData) {
      try {
        accessToken.value = newToken
        refreshToken.value = newRefreshToken
        user.value = JSON.parse(newUserData)

        // Schedule token refresh
        scheduleTokenRefresh()

        // Redirect to appropriate dashboard based on role
        if (user.value?.roles.includes('Admin')) {
          router.push('/admin')
        } else if (user.value?.roles.includes('Provider')) {
          router.push('/provider')
        } else if (user.value?.roles.includes('ServiceUser')) {
          router.push('/service-user/dashboard')
        } else if (user.value?.roles.includes('ShopStaff')) {
          router.push('/shop/dashboard')
        } else {
          router.push('/dashboard')
        }
      } catch (error) {
        console.error('Failed to sync login from other tab:', error)
        window.location.reload()
      }
    } else {
      // If data not available, just reload the page
      window.location.reload()
    }
  }

  // Schedule proactive token refresh before expiry
  function scheduleTokenRefresh(): void {
    // Clear any existing scheduled refresh
    if (refreshScheduleId.value) {
      clearTimeout(refreshScheduleId.value)
      refreshScheduleId.value = null
    }

    if (!accessToken.value) return

    try {
      const tokenPayload = JSON.parse(atob(accessToken.value.split('.')[1]))
      const expirationTime = tokenPayload.exp * 1000
      const currentTime = Date.now()
      const timeUntilExpiry = expirationTime - currentTime

      // Refresh token 1 minute before expiry
      // With 60-minute tokens, this gives plenty of time for refresh
      const refreshTime = Math.max(timeUntilExpiry - 60000, 0)

      if (refreshTime > 0) {
        console.log(`⏰ Next token refresh scheduled in ${Math.round(refreshTime / 1000)} seconds`)

        refreshScheduleId.value = window.setTimeout(async () => {
          console.log('⏰ Scheduled token refresh triggered')
          // Use force=true to ensure refresh happens even if client thinks token is still valid
          const success = await refreshTokens(true)
          if (!success) {
            console.warn('⚠️ Scheduled token refresh failed')
            // Don't log out here - the 401 handler will deal with it when next request fails
          }
        }, refreshTime)
      } else if (timeUntilExpiry <= 0) {
        // Token is already expired - try to refresh immediately instead of clearing auth
        console.warn('⚠️ Token is already expired, attempting immediate refresh...')
        // Use setTimeout to avoid blocking and allow other initialization to complete
        setTimeout(async () => {
          const success = await refreshTokens(true)
          if (!success) {
            console.warn('⚠️ Immediate token refresh failed')
            // Don't clear auth data here - let the 401 handler deal with invalid tokens
            // This prevents race conditions with concurrent requests
          }
        }, 0)
      }
    } catch (error) {
      console.error('Failed to schedule token refresh:', error)
      // Don't clear auth data on parse errors - token might still work
      // Let the backend validate and return 401 if truly invalid
    }
  }

  return {
    // State
    user,
    accessToken,
    refreshToken,
    isLoading,
    loginAttempts,
    lastLoginAttempt,
    isRefreshing,
    requiresTwoFactor,
    twoFactorUserId,
    twoFactorMethod,

    // Getters
    isAuthenticated,
    userRoles,
    userPermissions,
    userName,
    userInitials,
    isEmailVerified,
    isTwoFactorEnabled,
    isAdmin,
    isProvider,
    isSystemAdmin,
    canAttemptLogin,
    loginCooldownTime,
    isTokenExpired,
    isTokenNearExpiry,

    // Actions
    login,
    verifyTwoFactor,
    sendEmailCodeForLogin,
    register,
    logout,
    refreshTokens,
    forgotPassword,
    resetPassword,
    verifyEmail,
    resendVerificationEmail,
    getCurrentUser,
    updateProfile,

    // Permission checking
    hasRole,
    hasAnyRole,
    hasPermission,
    hasAnyPermission,
    hasAllPermissions,

    // Helpers
    initializeAuth,
    scheduleTokenRefresh,
    clearAuthData,
    setAuthData
  }
})